import {
  useEffect,
  useState,
  memo,
  useRef,
  useMemo,
} from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  IconButton,
  Stack,
  Divider,
  Grid,
  Tooltip,
} from '@mui/material';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddIconMui from '@mui/icons-material/Add';
import RemoveIconMui from '@mui/icons-material/Remove';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

import CustomText from 'components/common/CustomText/CustomText';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import SuggestedCPTs, { SuggestedCPTsRef } from '../SuggestedCPTs/SuggestedCPTs';
import { useSearchCPTs } from 'hooks/useLookups';
import { FieldPath, useFormContext, useWatch } from 'react-hook-form';

import useGlobalStyles from 'styles/globalStyles';
import useStyles from './styles';
import { colors } from 'styles/colors';
import AddIcon from 'icons/AddIcon';
import EmptySearch from 'assets/emptySearch.png';

import { CPT } from 'types/types/Types';
import { procedureDataType } from 'types/types';

interface Props {
  name: FieldPath<procedureDataType>;
  title: string;
  procedureNamePath: FieldPath<procedureDataType>;
}

/* ---------------- debounce hook ---------------- */

function useDebouncedValue<T>(value: T, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);

  return debounced;
}

/* ---------------- component ---------------- */

function SelectCPTs({ name, title, procedureNamePath }: Props) {
  const { control, setValue } = useFormContext<procedureDataType>();

  const selectedCPTs =
    (useWatch({ control, name }) as CPT[]) ?? [];


  const rawProcedureName = useWatch({
    control,
    name: procedureNamePath,
  });

  const procedureName =
    typeof rawProcedureName === 'string'
      ? rawProcedureName
      : '';

  const styles = useStyles();
  const globalStyles = useGlobalStyles();

  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const debouncedKeyword = useDebouncedValue(keyword, 300);

  const [cpts, setCPTs] = useState<CPT[]>([]);
  const [apiSelected, setApiSelected] = useState<CPT[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  const suggestionsRef = useRef<SuggestedCPTsRef>(null);

  const { refetch } = useSearchCPTs(debouncedKeyword, {
    enabled: false,
    onSuccess: (data: CPT[]) => setCPTs(data),
  });

  /* ---------------- totals (derived, memoized) ---------------- */

  const { totalRVUs, totalReimbursable } = useMemo(() => {
    if (!selectedCPTs.length) {
      return { totalRVUs: 0, totalReimbursable: 0 };
    }

    const total = selectedCPTs.reduce(
      (sum, cpt) => sum + cpt.rvu_value * (cpt.quantity ?? 1),
      0
    );

    const maxRVU = Math.max(
      ...selectedCPTs.map(c => c.rvu_value)
    );

    const reimbursable =
      maxRVU + (total - maxRVU) * 0.5;

    return {
      totalRVUs: total,
      totalReimbursable: reimbursable,
    };
  }, [selectedCPTs]);

  /* ---------------- search effect ---------------- */

  useEffect(() => {
    if (debouncedKeyword.trim()) {
      refetch();
    } else {
      setCPTs([]);
    }
  }, [debouncedKeyword, refetch]);

  /* ---------------- handlers ---------------- */

  const openModal = () => {
    setOpen(true);
    setKeyword('');
    setCPTs([]);
    setApiSelected(selectedCPTs);
  };

  const toggleCPT = (cpt: CPT) => {
    setApiSelected(prev =>
      prev.some(p => p.id === cpt.id)
        ? prev.filter(p => p.id !== cpt.id)
        : [...prev, { ...cpt, quantity: cpt.quantity ?? 1 }]
    );
  };

  const updateQuantity = (
    id: string,
    delta: number,
    outer = false
  ) => {
    const list = outer ? selectedCPTs : apiSelected;

    const updated = list.map(c =>
      c.id === id
        ? {
          ...c,
          quantity: Math.max(
            1,
            (c.quantity ?? 1) + delta
          ),
        }
        : c
    );

    outer
      ? setValue(name, updated, { shouldDirty: true })
      : setApiSelected(updated);
  };

  const saveSelection = () => {
    setValue(
      name,
      apiSelected.map(c => ({
        ...c,
        quantity: c.quantity ?? 1,
      })),
      { shouldDirty: true }
    );
    setOpen(false);
  };

  /* ---------------- render ---------------- */

  return (
    <>
      <Box className={globalStyles.sectionContainer}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          className={globalStyles.sectionHeader}
        >
          <CustomText
            text={title}
            color={colors.primary}
            fontWeight={600}
          />

          <Box mr={2} gap={1} display="flex">
            <Tooltip title="Get CPT Suggestions">
              <IconButton
                loading={suggestionsLoading}
                onClick={() =>
                  suggestionsRef.current?.fetchSuggestions()
                }
                sx={{
                  backgroundColor: colors.topBackground,
                  width: 35,
                  height: 35,
                  borderRadius: '4px',
                }}
              >
                {!suggestionsLoading && (
                  <AutoFixHighIcon
                    sx={{
                      color: colors.primary,
                      fontSize: 22,
                    }}
                  />
                )}
              </IconButton>
            </Tooltip>

            <Tooltip title="Add CPTs">
              <IconButton
                onClick={openModal}
                sx={{
                  backgroundColor: colors.topBackground,
                  width: 35,
                  height: 35,
                  borderRadius: '4px',
                }}
              >
                <AddIcon
                  size={22}
                  color={colors.primary}
                />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>

        {selectedCPTs.length > 0 && (
          <>
            <Divider />

            {totalRVUs > 0 && (
              <Stack px={2.5} pt={2} spacing={1}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                >
                  <CustomText
                    text="Total RVUs"
                    fontWeight={600}
                    color={colors.black}
                    fontSize={15}
                  />
                  <CustomText
                    text={totalRVUs.toFixed(3)}
                    fontWeight={600}
                    color={colors.black}
                    fontSize={15}
                  />
                </Stack>

                {selectedCPTs.length > 1 && (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                  >
                    <CustomText
                      text="Total Reimbursable RVUs"
                      fontWeight={600}
                      color={colors.black}
                      fontSize={15}
                    />
                    <CustomText
                      text={totalReimbursable.toFixed(3)}
                      fontWeight={600}
                      color={colors.black}
                      fontSize={15}
                    />
                  </Stack>
                )}
              </Stack>
            )}

            <Grid
              container
              spacing={2}
              sx={{ px: 2.5, my: 2 }}
            >
              {selectedCPTs.map((cpt, index) => (
                <Grid
                  size={{ xs: 12, md: 6 }}
                  key={`${cpt.id}-${index}`}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    p={1}
                    borderRadius="10px"
                    border={`1px solid ${colors.itemBorder}`}
                  >
                    <Box>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                      >
                        <CustomText
                          text={cpt.code}
                          color={colors.primary}
                        />
                        <IconButton
                          size="small"
                          onClick={() =>
                            updateQuantity(
                              cpt.id!,
                              -1,
                              true
                            )
                          }
                        >
                          <RemoveIconMui fontSize="small" />
                        </IconButton>
                        <CustomText
                          text={String(
                            cpt.quantity ?? 1
                          )}
                        />
                        <IconButton
                          size="small"
                          onClick={() =>
                            updateQuantity(
                              cpt.id!,
                              1,
                              true
                            )
                          }
                        >
                          <AddIconMui fontSize="small" />
                        </IconButton>
                      </Stack>
                      <CustomText text={cpt.value} />
                    </Box>

                    <IconButton
                      size="small"
                      sx={{ color: colors.error }}
                      onClick={() =>
                        setValue(
                          name,
                          selectedCPTs.filter(
                            c => c.id !== cpt.id
                          ),
                          { shouldDirty: true }
                        )
                      }
                    >
                      <RemoveCircleOutlineIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        <SuggestedCPTs
          ref={suggestionsRef}
          procedureName={procedureName}
          selectedCPTs={selectedCPTs}
          setSelectedCPTs={cpts =>
            setValue(name, cpts, { shouldDirty: true })
          }
          onLoadingChange={setSuggestionsLoading}
        />
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        className={globalStyles.modalContainer}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              boxShadow: '0px 12px 48px rgba(0,0,0,0.2)',
              overflow: 'hidden',
              height: 600,
            },
          },
        }}
      >
        <DialogTitle className={globalStyles.modalTitle}>
          {title}
        </DialogTitle>

        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {/* scrollable content */}
          <Stack spacing={2} sx={{ flex: 1, overflow: 'auto' }}>
            <TextField
              fullWidth
              placeholder="Search"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
            />

            {cpts.length === 0 && (
              <Box textAlign="center" pt={3}>
                <img src={EmptySearch} alt="" />
                <CustomText
                  text="No results to show"
                  color={colors.gray}
                />
              </Box>
            )}

            <List dense>
              {cpts.map(cpt => (
                <ListItem
                  key={cpt.id}
                  onClick={() => toggleCPT(cpt)}
                  className={styles.resultItem}
                >
                  <Checkbox
                    checked={apiSelected.some(a => a.id === cpt.id)}
                  />
                  <ListItemText
                    primary={cpt.code}
                    secondary={cpt.value}
                  />
                </ListItem>
              ))}
            </List>
          </Stack>

          {/* pinned footer */}
          <CustomButton
            text="Save"
            onClick={saveSelection}
            fullWidth
          />
        </DialogContent>

      </Dialog>
    </>
  );
}

export default memo(SelectCPTs);
